import React, { useRef, useState } from 'react';
import { HelpTooltip } from './HelpTooltip';

interface FileUploadWidgetProps {
  label: string;
  value?: string; // data-url
  onChange: (value: string) => void;
  error?: string;
  disabled?: boolean;
  description?: string;
  accept?: string; // e.g., "image/*"
  maxSizeMB?: number;
  helpText?: string;
}

export const FileUploadWidget: React.FC<FileUploadWidgetProps> = ({
  label,
  value,
  onChange,
  error,
  disabled = false,
  description,
  accept = 'image/*',
  maxSizeMB = 10,
  helpText
}) => {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [dragActive, setDragActive] = useState(false);
  const [uploading, setUploading] = useState(false);

  const handleFile = async (file: File) => {
    // Validate file type
    if (accept && !file.type.match(accept.replace('*', '.*'))) {
      alert(`Invalid file type. Expected: ${accept}`);
      return;
    }

    // Validate file size
    const fileSizeMB = file.size / (1024 * 1024);
    if (fileSizeMB > maxSizeMB) {
      alert(`File too large. Maximum size: ${maxSizeMB}MB`);
      return;
    }

    setUploading(true);
    try {
      // Convert to data URL
      const reader = new FileReader();
      reader.onload = (e) => {
        const dataUrl = e.target?.result as string;
        onChange(dataUrl);
        setUploading(false);
      };
      reader.onerror = () => {
        alert('Failed to read file');
        setUploading(false);
      };
      reader.readAsDataURL(file);
    } catch (err) {
      console.error('Error reading file:', err);
      alert('Failed to read file');
      setUploading(false);
    }
  };

  const handleDrag = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === 'dragenter' || e.type === 'dragover') {
      setDragActive(true);
    } else if (e.type === 'dragleave') {
      setDragActive(false);
    }
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);

    if (disabled) return;

    const files = e.dataTransfer.files;
    if (files && files.length > 0) {
      handleFile(files[0]);
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (disabled) return;

    const files = e.target.files;
    if (files && files.length > 0) {
      handleFile(files[0]);
    }
  };

  const handleClick = () => {
    if (!disabled) {
      fileInputRef.current?.click();
    }
  };

  const handleClear = (e: React.MouseEvent) => {
    e.stopPropagation();
    onChange('');
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  return (
    <div className="mb-4">
      {/* Label */}
      <div className="widget-label-row mb-2">
        <label className="block text-sm font-medium text-nordic-gray-700">
          {label}
        </label>
        {helpText && <HelpTooltip helpText={helpText} />}
      </div>

      {/* Description */}
      {description && (
        <p className="text-sm text-nordic-gray-500 mb-2">{description}</p>
      )}

      {/* Upload Area */}
      <div
        className={`relative border-2 border-dashed rounded-lg transition-colors ${
          dragActive
            ? 'border-nordic-blue-500 bg-nordic-blue-50'
            : error
            ? 'border-red-300 bg-red-50'
            : 'border-nordic-gray-300 bg-white hover:border-nordic-gray-400'
        } ${disabled ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}`}
        onDragEnter={handleDrag}
        onDragLeave={handleDrag}
        onDragOver={handleDrag}
        onDrop={handleDrop}
        onClick={handleClick}
      >
        <input
          ref={fileInputRef}
          type="file"
          accept={accept}
          onChange={handleChange}
          disabled={disabled}
          className="hidden"
        />

        {uploading ? (
          <div className="flex flex-col items-center justify-center py-12">
            <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-nordic-blue-500 mb-2"></div>
            <p className="text-sm text-nordic-gray-600">Uploading...</p>
          </div>
        ) : value && (value.startsWith('data:') || value.startsWith('http://') || value.startsWith('https://') || value.startsWith('blob:')) ? (
          <div className="relative">
            {/* Preview — render <video> when this widget is configured for
                video uploads (or when the value's MIME prefix says video),
                otherwise render <img>. */}
            <div className="p-4">
              {(accept?.startsWith('video/') || value.startsWith('data:video/')) ? (
                <video
                  src={value}
                  controls
                  className="max-w-full max-h-64 mx-auto rounded-md bg-black"
                />
              ) : (
                <img
                  src={value}
                  alt="Preview"
                  className="max-w-full max-h-64 mx-auto rounded-md"
                />
              )}
            </div>

            {/* Clear Button */}
            {!disabled && (
              <button
                type="button"
                onClick={handleClear}
                className="absolute top-2 right-2 bg-red-500 hover:bg-red-600 text-white 
                         rounded-full p-2 shadow-md transition-colors"
                title="Remove image"
              >
                <svg
                  className="w-4 h-4"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M6 18L18 6M6 6l12 12"
                  />
                </svg>
              </button>
            )}

            {/* File Info */}
            <div className="px-4 pb-4 text-center">
              <p className="text-xs text-nordic-gray-500">
                Click or drag to replace
              </p>
            </div>
          </div>
        ) : (
          <div className="flex flex-col items-center justify-center py-12 px-4">
            {/* Upload Icon */}
            <svg
              className="w-12 h-12 text-nordic-gray-400 mb-3"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12"
              />
            </svg>

            {/* Upload Text */}
            <p className="text-sm font-medium text-nordic-gray-700 mb-1">
              Click to upload or drag and drop
            </p>
            <p className="text-xs text-nordic-gray-500">
              {accept === 'image/*' ? 'PNG, JPG, GIF' : accept} up to {maxSizeMB}MB
            </p>
          </div>
        )}
      </div>

      {/* Error Message */}
      {error && (
        <p className="mt-2 text-sm text-red-600">{error}</p>
      )}
    </div>
  );
};

